import { useState, type MouseEvent, type ReactNode } from 'react';
import {
  List,
  Datagrid,
  TextInput,
  BooleanInput,
  DateInput,
  ShowButton,
  DeleteButton,
  ExportButton,
  TopToolbar,
  FunctionField,
  useRecordContext,
  Count,
} from 'react-admin';
import { Box, Typography, Avatar, IconButton, Tooltip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import GroupIcon from '@mui/icons-material/Group';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';

// --- Renk paleti (theme.ts ile uyumlu) ---
const COLORS = {
  primary: '#7367f0',
  primaryDark: '#5549d9',
  success: '#28c76f',
  error: '#ea5455',
  textPrimary: '#d0d2d6',
  textSecondary: '#676d7d',
  card: '#283046',
  header: '#242b3d',
  bg: '#161d31',
  border: '#404656',
};

// --- Tarih yardımcıları (ek bağımlılık yok) ---
const formatRelativeTime = (value?: string | Date): string => {
  if (!value) return '—';
  const d = new Date(value);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 0) return 'az sonra';
  if (diff < 60) return 'az önce';
  if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)} gün önce`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)} ay önce`;
  return `${Math.floor(diff / 31536000)} yıl önce`;
};

const formatFullDate = (value?: string | Date): string => {
  if (!value) return 'Kayıt yok';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

// --- Kopyalanabilir metin parçası ---
const CopyableText = ({ value, label }: { value?: string; label: string }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;

  const handleCopy = (e: MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };

  return (
    <Tooltip title={copied ? 'Kopyalandı!' : `${label} kopyala`} arrow>
      <IconButton
        size="small"
        onClick={handleCopy}
        sx={{
          ml: 0.5,
          p: 0.25,
          color: copied ? COLORS.success : COLORS.textSecondary,
          opacity: copied ? 1 : 0,
          transition: 'opacity 0.15s, color 0.15s',
          '.copyable-row:hover &': { opacity: 1 },
          '&:hover': { color: COLORS.primary, backgroundColor: 'transparent' },
        }}
      >
        {copied ? (
          <CheckIcon sx={{ fontSize: 14 }} />
        ) : (
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

// --- Kullanıcı hücresi (avatar + kimlikler) ---
const UserCell = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box
      className="copyable-row"
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}
    >
      <Avatar
        sx={{
          width: 42,
          height: 42,
          background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
          boxShadow: '0 4px 12px rgba(115, 103, 240, 0.35)',
        }}
      >
        <PersonIcon sx={{ fontSize: 22, color: '#fff' }} />
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: COLORS.textPrimary,
              fontFamily: 'monospace',
              letterSpacing: '0.02em',
            }}
          >
            {record.uuid ? `${record.uuid.substring(0, 8)}…` : '—'}
          </Typography>
          <CopyableText value={record.uuid} label="UUID" />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: COLORS.textSecondary,
            fontFamily: 'monospace',
            fontSize: '0.7rem',
          }}
        >
          {record.firebaseUid
            ? `UID: ${record.firebaseUid.substring(0, 14)}…`
            : 'UID yok'}
        </Typography>
      </Box>
    </Box>
  );
};

// --- Durum rozeti (pulse nokta ile) ---
const StatusBadge = () => {
  const record = useRecordContext();
  if (!record) return null;

  const active = record.isActive;
  const color = active ? COLORS.success : COLORS.error;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: '20px',
        backgroundColor: `${color}1f`,
        border: `1px solid ${color}33`,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: color,
          boxShadow: `0 0 0 ${active ? '3px' : '0px'} ${color}33`,
          animation: active ? 'pulse 2s infinite' : 'none',
          '@keyframes pulse': {
            '0%': { boxShadow: `0 0 0 0 ${color}66` },
            '70%': { boxShadow: `0 0 0 6px ${color}00` },
            '100%': { boxShadow: `0 0 0 0 ${color}00` },
          },
        }}
      />
      <Typography
        sx={{ fontWeight: 600, fontSize: '0.75rem', color, lineHeight: 1 }}
      >
        {active ? 'Aktif' : 'Pasif'}
      </Typography>
    </Box>
  );
};

// --- Tarih hücresi (relative + tam tarih) ---
const DateCell = ({
  source,
  icon,
}: {
  source: string;
  icon: ReactNode;
}) => {
  const record = useRecordContext();
  if (!record) return null;
  const value = record[source];

  return (
    <Tooltip title={formatFullDate(value)} arrow placement="top">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '8px',
            backgroundColor: COLORS.bg,
            color: COLORS.textSecondary,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ color: COLORS.textPrimary, fontWeight: 500 }}
          >
            {formatRelativeTime(value)}
          </Typography>
          <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
            {formatFullDate(value)}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

const userFilters = [
  <TextInput
    key="uuid"
    label="UUID / Kullanıcı ID ile ara"
    source="uuid"
    alwaysOn
    variant="outlined"
  />,
  <BooleanInput key="isActive" label="Aktif" source="isActive" />,
  <DateInput key="createdAt_gte" label="Başlangıç Tarihi" source="createdAt_gte" />,
  <DateInput key="createdAt_lte" label="Bitiş Tarihi" source="createdAt_lte" />,
];

const ListActions = () => (
  <TopToolbar>
    <ExportButton
      sx={{
        backgroundColor: COLORS.primary,
        color: 'white',
        '&:hover': { backgroundColor: COLORS.primaryDark },
      }}
    />
  </TopToolbar>
);

export const UserList = () => (
  <Box>
    {/* Başlık */}
    <Box
      sx={{
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '12px',
            background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
            boxShadow: '0 4px 16px rgba(115, 103, 240, 0.4)',
          }}
        >
          <GroupIcon sx={{ color: '#fff', fontSize: 26 }} />
        </Box>
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: COLORS.textPrimary, lineHeight: 1.2 }}
          >
            Kullanıcılar
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.textSecondary, mt: 0.25 }}>
            Tüm kayıtlı kullanıcıları yönetin ·{' '}
            <Count
              resource="users"
              sx={{
                display: 'inline',
                color: COLORS.primary,
                fontWeight: 600,
              }}
            />{' '}
            kayıt
          </Typography>
        </Box>
      </Box>
    </Box>

    <List
      filters={userFilters}
      actions={<ListActions />}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
      sx={{
        '& .RaList-main': {
          backgroundColor: COLORS.card,
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.24)',
        },
      }}
    >
      <Datagrid
        rowClick="show"
        bulkActionButtons={false}
        sx={{
          '& .RaDatagrid-headerCell': {
            backgroundColor: COLORS.header,
            fontWeight: 600,
            color: COLORS.textSecondary,
            fontSize: '0.72rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            borderColor: COLORS.border,
            py: 1.5,
          },
          '& .RaDatagrid-row': {
            transition: 'background-color 0.15s ease, transform 0.15s ease',
            cursor: 'pointer',
          },
          '& .RaDatagrid-row:hover': {
            backgroundColor: `${COLORS.header} !important`,
          },
          '& .RaDatagrid-rowCell': {
            borderColor: COLORS.border,
            py: 1.5,
          },
          // Sıralama oklarını belirginleştir
          '& .RaDatagrid-headerCell .MuiTableSortLabel-icon': {
            color: `${COLORS.primary} !important`,
          },
          '& .MuiTableSortLabel-root.Mui-active': {
            color: `${COLORS.textPrimary} !important`,
          },
        }}
      >
        <FunctionField label="Kullanıcı" sortBy="uuid" render={() => <UserCell />} />
        <FunctionField
          label="Durum"
          sortBy="isActive"
          render={() => <StatusBadge />}
        />
        <FunctionField
          label="Kayıt Tarihi"
          sortBy="createdAt"
          render={() => (
            <DateCell
              source="createdAt"
              icon={<EventIcon sx={{ fontSize: 16 }} />}
            />
          )}
        />
        <FunctionField
          label="Son Senkronizasyon"
          sortBy="lastSyncAt"
          render={() => (
            <DateCell
              source="lastSyncAt"
              icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
            />
          )}
        />
        <FunctionField
          label="İşlemler"
          render={() => (
            <Box
              sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}
              onClick={(e) => e.stopPropagation()}
            >
              <ShowButton />
              <DeleteButton mutationMode="pessimistic" />
            </Box>
          )}
        />
      </Datagrid>
    </List>
  </Box>
);
